/**
 * ControlAssign frame: server→client "you now drive PlayerId N". (§4.8, §9.3)
 *
 * The server owns which PlayerId a connection drives. Welcome (welcome.ts) hands
 * out the FIRST one; this frame reassigns it whenever that changes — a manual
 * switch the server honoured (InputFlags.SwitchPlayer), an auto-switch on a
 * possession change, or a drop-in takeover. It is the authoritative counterpart
 * to the client's optimistic PlayerSwitcher: the client may move its belief the
 * instant it flicks, but THIS is what actually decides it, and the two are made
 * to agree by both computing selection through shared/src/switching.ts.
 *
 * ── Layout (payload only; the MessageType byte is added by protocol.ts) ──────
 *
 *   playerId[CONTROL_PLAYER_BITS]     0..MAX_PLAYERS-1, 8 bits (headroom to 255)
 *
 * One byte of payload. It is deliberately its own tiny frame rather than a field
 * on the snapshot: a switch is an event, not per-tick state, and folding it into
 * the 20Hz snapshot would either delay the handoff by up to a snapshot interval
 * or force a field onto every snapshot that is set on almost none of them.
 *
 * CHANGING THIS WIDTH IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION (§12.6).
 */

import { MAX_PLAYERS } from '../sim/types.js';
import { BitReader, BitWriter } from './bitwriter.js';

/** 8 bits covers 0..MAX_PLAYERS-1 (=21) with headroom, matching Welcome's id width. */
export const CONTROL_PLAYER_BITS = 8;

/** Write a ControlAssign payload into `w` (no type byte — protocol.ts adds it). */
export function encodeControlAssign(w: BitWriter, playerId: number): void {
  w.writeBits(playerId & 0xff, CONTROL_PLAYER_BITS);
}

/**
 * Read a ControlAssign payload. Returns the assigned PlayerId, or -1 if the frame
 * was truncated or names a slot outside [0, MAX_PLAYERS) — a hostile or corrupt
 * frame must never reassign control to a nonexistent player. Check `r.overflow`
 * for the truncation case if the distinction matters to the caller.
 */
export function decodeControlAssign(r: BitReader): number {
  const id = r.readBits(CONTROL_PLAYER_BITS);
  if (r.overflow) return -1;
  if (id < 0 || id >= MAX_PLAYERS) return -1;
  return id;
}
